import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";

/* ======================================================
   TYPES
====================================================== */

export interface Settings {
  theme: "light" | "dark";
  privacy: {
    lastSeen: boolean;
    profilePhoto: boolean;
  };
  security: {
    twoFactor: boolean;
  };
  notifications: {
    sound: {
      contacts: boolean;
      messages: boolean;
    };
  };
  ui: {
    defaultTab: string;
  };
  media: {
    avatar: string;
    cover: string;
  };
}

interface SettingsContextData {
  settings: Settings | null;
  loading: boolean;
  updateSettings: (data: Partial<Settings>) => void;
  resetSettings: () => void;
}

/* ======================================================
   CONTEXT
====================================================== */

const SettingsContext = createContext<SettingsContextData>(
  {} as SettingsContextData
);

/* ======================================================
   DEFAULT SETTINGS
====================================================== */

const defaultSettings: Settings = {
  theme: "light",
  privacy: {
    lastSeen: true,
    profilePhoto: true,
  },
  security: {
    twoFactor: false,
  },
  notifications: {
    sound: {
      contacts: true,
      messages: true,
    },
  },
  ui: {
    defaultTab: "contacts",
  },
  media: {
    avatar: "/assets/images/users/user-dummy-img.jpg",
    cover: "/assets/images/small/img-4.jpg",
  },
};

/* ======================================================
   PROVIDER
====================================================== */

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);

  const initializedRef = useRef(false);

  /* --------------------------------------------------
     LOAD SETTINGS
  -------------------------------------------------- */
  const loadSettings = useCallback(
    (userId: number) => {
      const storageKey = `settings_${userId}`;

      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          setSettings(JSON.parse(stored));
        } else {
          localStorage.setItem(storageKey, JSON.stringify(defaultSettings));
          setSettings(defaultSettings);
        }
      } catch (err) {
        console.error("[SettingsContext] erro ao carregar settings", err);
        setSettings(defaultSettings);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /* --------------------------------------------------
     PRELOAD (1x por login)
  -------------------------------------------------- */
  useEffect(() => {
    if (!user?.id) return;

    const storageKey = `settings_${user.id}`;

    // console.log("[SettingsProvider] lendo:", storageKey);

    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setSettings(parsed);
        // console.log("[SettingsProvider] settings carregado", parsed);
      } catch (err) {
        console.error("[SettingsProvider] JSON inválido", err);
        setSettings(defaultSettings);
      }
    } else {
      console.warn("[SettingsProvider] settings não encontrado, criando default");
      localStorage.setItem(storageKey, JSON.stringify(defaultSettings));
      setSettings(defaultSettings);
    }

    setLoading(false);
  }, [user?.id]);




  // useEffect(() => {
  //   if (!user?.id) return;
  //   if (initializedRef.current) return;

  //   initializedRef.current = true;
  //   loadSettings(user.id);
  // }, [user?.id, loadSettings]);

  /* --------------------------------------------------
     UPDATE SETTINGS
  -------------------------------------------------- */
  const updateSettings = useCallback(
    (data: Partial<Settings>) => {
      if (!user?.id || !settings) return;

      const updated = {
        ...settings,
        ...data,
      };

      setSettings(updated);
      localStorage.setItem(
        `settings_${user.id}`,
        JSON.stringify(updated)
      );
    },
    [settings, user?.id]
  );

  /* --------------------------------------------------
     RESET SETTINGS
  -------------------------------------------------- */
  const resetSettings = useCallback(() => {
    if (!user?.id) return;

    setSettings(defaultSettings);
    localStorage.setItem(
      `settings_${user.id}`,
      JSON.stringify(defaultSettings)
    );
  }, [user?.id]);

  /* --------------------------------------------------
     PROVIDER
  -------------------------------------------------- */
  return (
    <SettingsContext.Provider
      value={{
        settings,
        loading,
        updateSettings,
        resetSettings,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

/* ======================================================
   HOOK
====================================================== */

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within SettingsProvider");
  }
  return context;
};
